import React, { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import ReservationService from '~/services/ReservationService';

export interface PropertyImage {
  id: string;
  id_cloudinary_external: string;
  property_id: string;
  order?: number;
  title?: string;
  filename?: string;
}

export interface Property {
  id: string;
  name: string;
  geo_data?: {
    country?: string | null;
    locality?: string | null;
  };
  images?: Array<PropertyImage>;
}

interface PropertyAutocompleteProps {
  value: string;
  onPropertySelect: (property: Property | null) => void;
  onInputChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export function PropertyAutocomplete({
  value,
  onPropertySelect,
  onInputChange,
  error,
  helperText,
  disabled,
  label,
  placeholder,
  required = false,
}: PropertyAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const {
    data: options = [],
    isLoading,
    error: searchError,
  } = useQuery({
    queryKey: ['properties', 'search', debouncedSearchTerm],
    queryFn: async () => {
      const response = await ReservationService.getPropertiesByName(debouncedSearchTerm, 10, 1, false);
      return response.result || [];
    },
    enabled: debouncedSearchTerm.length >= 2,
  });

  const searchProperties = useCallback(
    debounce(async (searchTerm: string) => {
      if (searchTerm.length < 2) {
        return;
      }

      setDebouncedSearchTerm(searchTerm);
    }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      searchProperties.cancel();
    };
  }, [searchProperties]);

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, newInputValue: string) => {
      setInputValue(newInputValue);
      onInputChange(newInputValue);
      searchProperties(newInputValue);
    },
    [onInputChange, searchProperties],
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: Property | string | null) => {
      if (typeof newValue === 'object' && newValue !== null) {
        onPropertySelect(newValue);
        setInputValue(newValue.name);
        onInputChange(newValue.name);
      } else if (typeof newValue === 'string') {
        onInputChange(newValue);
      } else {
        onPropertySelect(null);
      }
    },
    [onPropertySelect, onInputChange],
  );

  const isOptionEqualToValue = (option: string | Property, val: string | Property) => {
    const optionValue = typeof option === 'string' ? option : option.name;
    const valValue = typeof val === 'string' ? val : val.name;

    return optionValue === valValue;
  };

  return (
    <Autocomplete
      freeSolo
      options={searchError ? [] : options}
      loading={isLoading}
      disabled={disabled}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          slotProps={{
            inputLabel: { shrink: !!inputValue },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
          data-testid="offer-name"
        />
      )}
      renderOption={(props, option) => {
        if (typeof option === 'string') {
          return null;
        }
        return (
          <li {...props} key={option.id}>
            {option.name}
            {option.geo_data?.locality && `, ${option.geo_data.locality}`}
            {option.geo_data?.country && ` - ${option.geo_data.country}`}
          </li>
        );
      }}
    />
  );
}
